import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useUserProfile, useUserAvatars } from './useAvatarCollection';
import { useAuth } from '../auth/useAuth';
import AvatarCard from './AvatarCard';
import MiniAvatarCanvas from '../../components/MiniAvatarCanvas';
import './gallery.css';

export default function PublicProfilePage() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { data: currentUser } = useAuth();
  const { data: profile, isLoading: profileLoading } = useUserProfile(key ?? '');
  const { data: avatars, isLoading: avatarsLoading } = useUserAvatars(key ?? '');

  const isLoading = profileLoading || avatarsLoading;
  const isSelf = !!currentUser && currentUser.id === profile?.id;

  // Old bookmarks / links used the username; once we know the canonical id,
  // settle the address bar on it so future shares/reshares are rename-proof.
  useEffect(() => {
    if (profile && key !== String(profile.id)) {
      navigate(`/users/${profile.id}`, { replace: true });
    }
  }, [profile, key, navigate]);

  if (isLoading) {
    return (
      <div className="malpedia-gallery-page">
        <div className="malpedia-gallery-loading">Loading...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="malpedia-gallery-page">
        <div className="malpedia-gallery-loading">User not found</div>
      </div>
    );
  }

  if (!profile.is_public && !isSelf) {
    return (
      <div className="malpedia-gallery-page">
        <div className="malpedia-private-profile">
          <span className="malpedia-private-profile-icon">{'\uD83D\uDD12'}</span>
          <h2>{profile.username}</h2>
          <p>This profile is private.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="malpedia-gallery-page">
      <div className="malpedia-public-profile-header">
        {profile.avatar_config && (
          <MiniAvatarCanvas
            config={profile.avatar_config}
            size={64}
          />
        )}
        <div className="malpedia-public-profile-info">
          <h2>{profile.username}</h2>
          <p>
            {profile.avatar_count ?? 0} avatar{(profile.avatar_count ?? 0) !== 1 ? 's' : ''}
          </p>
          {isSelf && !profile.is_public && (
            <p>
              Your profile is private. <Link to="/profile">Change visibility</Link>
            </p>
          )}
        </div>
      </div>

      {!avatars?.length ? (
        <div className="malpedia-gallery-empty">
          <p>No avatars to show.</p>
        </div>
      ) : (
        <div className="malpedia-gallery-grid">
          {avatars.map((a) => (
            <AvatarCard key={a.id} avatar={a} isOwner={isSelf} />
          ))}
        </div>
      )}
    </div>
  );
}
